import { Link } from 'react-router-dom';
import { ArrowLeft, MoreVertical, Archive, ArchiveRestore, Ban, ShieldCheck, ArrowUpRight, Trees, Package } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { imgUrl } from '@/lib/api';

export function Avatar({ user, size = 40, online }) {
  const initials = (user?.name || '?').split(' ').map(s => s[0]).slice(0, 2).join('').toUpperCase();
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      {user?.picture
        ? <img src={imgUrl(user.picture)} alt={user.name} className="w-full h-full rounded-full object-cover border border-white/10"/>
        : <div className="w-full h-full rounded-full grid place-items-center font-display text-sm text-obsidian"
               style={{ background: 'linear-gradient(135deg, rgb(180,255,0), #8dd8ff)' }}>{initials}</div>}
      {online && <span className="absolute bottom-0 right-0 w-3 h-3 rounded-full bg-[#8dd8ff] ring-2 ring-background" data-testid="avatar-online-dot"/>}
    </div>
  );
}

const euro = (n) => `€ ${new Intl.NumberFormat('it-IT').format(n)}`;

export function PinnedSubject({ conv }) {
  const l = conv?.listing;
  const b = conv?.batch;
  if (!l && !b) return null;
  const data = l ? {
    to: `/terreno/${l.listing_id}`, kind: l.listing_type === 'rent' ? 'Affitto' : 'Vendita', Icon: Trees,
    title: l.title, sub: [l.location?.comune, l.location?.region, l.area_hectares && `${l.area_hectares} ha`].filter(Boolean).join(' · '),
    price: l.price ? `${euro(l.price)}${l.listing_type === 'rent' ? (l.rental_period === 'monthly' ? '/mese' : '/anno') : ''}` : 'Prezzo su richiesta',
    cover: l.cover,
  } : {
    to: `/partite/${b.batch_id}`, kind: 'Partita', Icon: Package,
    title: `${b.fruit_type}${b.variety ? ` ${b.variety}` : ''}`, sub: [b.quantity_quintals && `${b.quantity_quintals} q`, b.region].filter(Boolean).join(' · '),
    price: b.price_per_quintal ? `${euro(b.price_per_quintal)}/q` : b.total_price ? euro(b.total_price) : 'Prezzo su richiesta',
    cover: b.cover,
  };
  return (
    <Link to={data.to} data-testid="chat-pinned-subject"
          className="group mx-3 sm:mx-4 mt-3 flex items-center gap-3 rounded-2xl glass-vp p-2.5 pr-4 transition-transform duration-300 hover:-translate-y-0.5">
      <div className="w-14 h-14 rounded-xl overflow-hidden border border-white/10 shrink-0 bg-secondary grid place-items-center">
        {data.cover ? <img src={imgUrl(data.cover)} alt={data.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"/>
          : <data.Icon size={20} className="text-chartreuse"/>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[9px] uppercase tracking-[0.26em] font-bold text-chartreuse">{data.kind}</div>
        <div className="font-display text-base leading-tight truncate" data-testid="chat-pinned-title">{data.title}</div>
        <div className="text-xs text-muted-foreground truncate">{data.sub}</div>
      </div>
      <div className="text-right shrink-0">
        <div className="font-display text-sm sm:text-base" data-testid="chat-pinned-price">{data.price}</div>
        <div className="hidden sm:inline-flex items-center gap-1 text-[11px] text-[#8dd8ff]">Apri annuncio <ArrowUpRight size={12} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"/></div>
      </div>
    </Link>
  );
}

export function ChatHeader({ conv, online, typing, wsConnected, onBack, onArchive, onBlockToggle }) {
  const u = conv?.other_user;
  return (
    <div className="h-16 shrink-0 border-b border-border bg-card/80 backdrop-blur-md flex items-center gap-3 px-3 sm:px-4 z-20">
      <button onClick={onBack} className="md:hidden w-9 h-9 rounded-full grid place-items-center hover:bg-secondary transition-colors"
              data-testid="chat-back-btn" aria-label="Indietro"><ArrowLeft size={18}/></button>
      <Avatar user={u} online={online}/>
      <div className="min-w-0 flex-1">
        <div className="font-display text-base sm:text-lg leading-tight truncate" data-testid="chat-peer-name">{u?.name || '—'}</div>
        <div className="text-[11px] h-4">
          {typing ? <span className="text-chartreuse">sta scrivendo…</span>
            : online ? <span className="text-[#8dd8ff]" data-testid="peer-online">Online</span>
            : <span className="text-muted-foreground">{wsConnected ? 'Offline · riceverà una notifica' : 'Riconnessione…'}</span>}
        </div>
      </div>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="w-9 h-9 rounded-full grid place-items-center hover:bg-secondary transition-colors" data-testid="chat-menu-btn" aria-label="Opzioni">
            <MoreVertical size={18}/>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-52">
          <DropdownMenuItem onClick={onArchive} data-testid="chat-archive-btn">
            {conv?.archived ? <><ArchiveRestore size={14} className="mr-2"/> Ripristina chat</> : <><Archive size={14} className="mr-2"/> Archivia chat</>}
          </DropdownMenuItem>
          <DropdownMenuSeparator/>
          <DropdownMenuItem onClick={onBlockToggle} data-testid="chat-block-btn" className={conv?.blocked_by_me ? '' : 'text-destructive focus:text-destructive'}>
            {conv?.blocked_by_me ? <><ShieldCheck size={14} className="mr-2"/> Sblocca utente</> : <><Ban size={14} className="mr-2"/> Blocca utente</>}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
